import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { BRAND } from '../config/brand';
import PasswordFields, { analyzePassword, passwordsMatch } from '../components/PasswordFields';

const inputClass =
  'w-full border border-slate-300 rounded-lg px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user, clearMustChangePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const passwordAnalysis = useMemo(
    () => analyzePassword(newPassword, user?.email || ''),
    [newPassword, user?.email]
  );
  const canSubmit =
    currentPassword.length > 0 &&
    passwordAnalysis.isValid &&
    passwordsMatch(newPassword, newPasswordConfirm);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error('Please meet all password requirements');
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch('/admin/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      clearMustChangePassword();
      toast.success('Password updated');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <form onSubmit={submit} className="bg-white p-8 rounded-xl shadow-md w-full max-w-md">
        <h1 className="text-2xl font-semibold text-teal-800 mb-1">{BRAND.companyName}</h1>
        <p className="text-slate-600 mb-1">Change your password</p>
        {user?.email && <p className="text-sm text-slate-500 mb-6">{user.email}</p>}

        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-5">
          You must set a new password before continuing.
        </p>

        <div className="mb-5">
          <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="current_password">
            Current password
          </label>
          <div className="relative">
            <input
              id="current_password"
              type={showCurrent ? 'text' : 'password'}
              className={inputClass}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-800"
            >
              {showCurrent ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <PasswordFields
          password={newPassword}
          confirmPassword={newPasswordConfirm}
          email={user?.email || ''}
          onPasswordChange={setNewPassword}
          onConfirmChange={setNewPasswordConfirm}
          passwordLabel="New password"
          confirmLabel="Confirm new password"
          passwordPlaceholder="Choose a strong new password"
          confirmPlaceholder="Re-enter your new password"
        />

        <button
          type="submit"
          disabled={submitting || !canSubmit}
          className="w-full mt-6 bg-teal-700 text-white py-2.5 rounded-lg hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </div>
  );
};

export default ChangePassword;
